import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

type BaseOption = {
  onClick?: (option: any) => void
}

export type ActionOption = {
  id: number
  label: string
  onClick?: (option: ActionOption) => void
}

type GenericDropdownProps<T extends BaseOption> = {
  options: T[]
  value?: T | null
  onChange?: (option: T) => void
  placeholder?: string
  disabled?: boolean
  getOptionLabel: (option: T) => string
  getOptionValue: (option: T) => string | number
  className?: string
}

const MENU_MIN_WIDTH = 160
const MENU_MAX_HEIGHT = 200

export function GenericDropdown<T extends BaseOption>({
  options,
  value = null,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  getOptionLabel,
  getOptionValue,
  className = ''
}: GenericDropdownProps<T>) {

  const [isOpen, setIsOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    if (!dropdownRef.current) return

    const rect = dropdownRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpwards = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow

    // El menú iguala el ancho del trigger (antes lo hacía con width:100%), con un mínimo
    // para que no quede ilegible cuando la píldora es muy angosta.
    const width = Math.max(rect.width, MENU_MIN_WIDTH)
    let left = rect.left
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))

    setMenuStyle({
      position: 'fixed',
      left,
      top: openUpwards ? undefined : rect.bottom + 4,
      bottom: openUpwards ? window.innerHeight - rect.top + 4 : undefined,
      width,
      minWidth: width,
      maxHeight: MENU_MAX_HEIGHT,
      zIndex: 1060,
    })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return

    updatePosition()

    // capture:true para enterarnos también del scroll de contenedores intermedios (tablas, drawers).
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const clickedTrigger = dropdownRef.current?.contains(target)
      // El menú vive en un portal, así que ya no cuelga de dropdownRef: sin este segundo
      // chequeo, el mousedown sobre una opción cerraría el menú antes de que corra su onClick.
      const clickedMenu = menuRef.current?.contains(target)

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: T) => {
    // Ejecuta cambio si existe
    if (onChange) {
      onChange(option)
    }

    // Ejecuta acción propia de la opción si existe
    if (option.onClick) {
      option.onClick(option)
    }

    setIsOpen(false)
  }

  return (
    <div className='nb-language-autocomplete cursor-pointer'>
        <div ref={dropdownRef} className={`${className}`}>
            <div
                className={`tap-add-experience-2-category-pill ${disabled ? 'disabled' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  !disabled && setIsOpen(prev => !prev)
                }}
            >
                {value ? getOptionLabel(value) : placeholder}
            </div>

            {isOpen && createPortal(
                <div ref={menuRef} className="nb-language-menu" style={menuStyle}>
                {options.map((option) => (
                    <div
                    key={getOptionValue(option)}
                    className="nb-language-item"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(option)
                    }}
                    >
                    {getOptionLabel(option)}
                    </div>
                ))}
                </div>,
                document.body
            )}
        </div>
    </div>
  )
}